// collab-drill.js — the list behind each number on the Analytics page's
// Collaboration card. Clicking a number opens the Coordination page filtered
// to the items that number counts, over the same 7/30/90-day window; the
// rows come from /v1/teams/analytics/collaboration, which counts exactly
// like the card. The filter rides in the URL (?collab=<kind>&days=<n>) so a
// refresh or a shared link keeps it.
import { chatLabel, friendlyResource } from "./format.js";

export const COLLAB_KINDS = {
  direct: { label: "Direct messages", tip: "Show these messages" },
  broadcast: { label: "Broadcasts", tip: "Show these messages" },
  reply: { label: "Replies", tip: "Show the replies behind this rate" },
  message: { label: "All messages", tip: "Show every message counted here" },
  note: { label: "Notes", tip: "Show these notes" },
  nag: { label: "Stale-lock reminders", tip: "Show these reminders" },
};

const WINDOW_DAYS = [7, 30, 90];

// Kind chip per event type, in the row's Kind column.
const ROW_KIND = {
  "message.sent": "direct",
  "message.broadcast": "broadcast",
  "message.replied": "reply",
  "note.added": "note",
  "lease.nudged": "reminder",
};

export function isCollabDrill(d) {
  return !!(d && COLLAB_KINDS[d.kind] && WINDOW_DAYS.includes(d.days));
}

export function collabFilterLabel(d) {
  return COLLAB_KINDS[d.kind].label + " · last " + d.days + " days";
}

// The reply rate is a ratio, so no list equals it; the list shows its
// numerator and this line says how the rate is made.
export function collabFilterNote(d) {
  return d.kind === "reply"
    ? "The reply rate is these replies divided by messages sent (direct and broadcast)."
    : "";
}

export function parseCollabQuery(search) {
  const p = new URLSearchParams(search || "");
  const kind = p.get("collab");
  if (!kind) return null;
  const d = { kind, days: Number(p.get("days")) };
  return isCollabDrill(d) ? d : null;
}

export function collabQueryString(d) {
  return "?collab=" + encodeURIComponent(d.kind) + "&days=" + d.days;
}

export function collabAPIPath(d, page, pageSize) {
  return (
    "/v1/teams/analytics/collaboration?kind=" + encodeURIComponent(d.kind) +
    "&window=" + d.days + "d&limit=" + pageSize + "&offset=" + page * pageSize
  );
}

function firstLine(text) {
  const t = String(text || "").trim().split("\n").find((l) => l.trim()) || "";
  return t.length > 160 ? t.slice(0, 157) + "…" : t;
}

// collabRow turns one API item into the row the list draws. hasText is the
// envelope's has_text: the local daemon sends the message/note text, the
// team server sends the event only (its resource already decrypted by the
// caller when the team key is loaded).
export function collabRow(item, hasText) {
  const type = item.event_type;
  const row = {
    kind: ROW_KIND[type] || type,
    agent_id: item.agent_id || "",
    machine_id: item.laptop_id || "",
    ts: item.occurred_at || "",
    thread_id: "",
    summary: "",
  };
  if (type === "lease.nudged") {
    const file = friendlyResource(item.resource || "");
    row.summary = file ? "Reminded to release " + file : "Reminded to release a file (name hidden)";
    return row;
  }
  if (type === "note.added") {
    if (!hasText) row.summary = "Pinned a note (text not shown here)";
    else row.summary = item.stored ? firstLine(item.body) : "(no longer on the bulletin)";
    return row;
  }
  if (!hasText) {
    row.summary = "Message text not shown here";
    return row;
  }
  if (!item.stored) {
    row.summary = "(message no longer stored)";
    return row;
  }
  row.thread_id = item.context_id || "";
  const said = firstLine(item.body) || firstLine(item.subject) || "(empty message)";
  row.summary = said + " — to " + (item.to ? chatLabel(item.to) : "everyone");
  return row;
}
